import { useCallback, useEffect } from 'react';
import { useMapEvents } from 'react-leaflet';

export interface MapBounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
  zoom: number;
}

interface BoundsObserverProps {
  /** Called with the current viewport bounds + zoom whenever the user
   *  pans or zooms, plus once on mount so the consumer doesn't have to
   *  wait for the first interaction. */
  onBoundsChange: (b: MapBounds) => void;
}

/**
 * Reports the map's viewport bounds + zoom on every move/zoom end. Used by
 * pages like the parcel-ownership map to clip an in-memory parcel cache to
 * the visible viewport (no server refetch) and to toggle dense ParcelNo
 * labels on only at high zoom.
 *
 * Extracted verbatim from the legacy MapComponent so callers can compose
 * this independently of the rest of the map surface.
 */
export function BoundsObserver({ onBoundsChange }: BoundsObserverProps) {
  const emit = useCallback(
    (map: L.Map) => {
      try {
        const b = map.getBounds();
        onBoundsChange({
          minLng: b.getWest(),
          minLat: b.getSouth(),
          maxLng: b.getEast(),
          maxLat: b.getNorth(),
          zoom: map.getZoom(),
        });
      } catch (err) {
        console.error('BoundsObserver: failed to read bounds', err);
      }
    },
    [onBoundsChange],
  );

  const map = useMapEvents({
    moveend: () => emit(map),
    zoomend: () => emit(map),
  });

  // Emit once on mount so the parent doesn't have to wait for a pan/zoom.
  useEffect(() => {
    if (map) emit(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

export default BoundsObserver;
